import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, BookOpen, Check, ChevronRight, Globe2, Headphones, TrendingUp, Moon, Sun, Pause, Play, Search, Rewind, FastForward, X } from 'lucide-react';
import { audioUrl, parseRoute, readState, saveState, verseAtTime } from './state';
import AudioTimeline from './AudioTimeline';
import { labels } from './i18n';
import './App.css';

const BASE = process.env.PUBLIC_URL || '';
const POPULAR = [1, 18, 36, 55, 56];
const ISTIADHAH = 'أَعُوذُ بِاللَّهِ مِنَ الشَّيْطَانِ الرَّجِيمِ';
const dataCache = new Map();
async function getData(path, signal) {
  if (dataCache.has(path)) return dataCache.get(path);
  const response = await fetch(`${BASE}/data/${path}`, { signal });
  if (!response.ok) throw new Error('Data unavailable');
  const data = await response.json();
  dataCache.set(path, data);
  return data;
}
function initialState() {
  try { return readState(window.localStorage); } catch { return readState({ getItem: () => null }); }
}
function IconButton({ label, children, ...props }) {
  return <button className="icon-button" aria-label={label} title={label} {...props}>{children}</button>;
}
export default function App() {
  const [settings, setSettings] = useState(initialState);
  const [surahs, setSurahs] = useState([]);
  const [audioTimings, setAudioTimings] = useState(null);
  const [route, setRoute] = useState(null);
  const [availability, setAvailability] = useState({ tk: false, en: true, ru: true });
  const [query, setQuery] = useState('');
  const [text, setText] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [passages, setPassages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playerVisible, setPlayerVisible] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [verse, setVerse] = useState(1);
  const [audioError, setAudioError] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const [credits, setCredits] = useState(false);
  const [recitationIntro, setRecitationIntro] = useState(false);
  const audio = useRef(null);
  const audioBar = useRef(null);
  const headerBar = useRef(null);
  const readerToolbar = useRef(null);
  const creditsDialog = useRef(null);
  const recitationDialog = useRef(null);
  const recitationStarted = useRef(false);
  const pendingRecitation = useRef(null);
  const restoring = useRef(false);
  const pendingSeek = useRef(null);
  const seekScrollTimer = useRef(null);
  const t = labels[settings.language];
  const chapter = surahs.find(s => s.number === route?.surah);
  const track = audioTimings?.[chapter?.number];

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = settings.theme;
    document.documentElement.style.setProperty('--translation-font-scale', settings.fontScale || 1);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', settings.theme === 'dark' ? '#151e1a' : '#f8f7f3');
  }, [settings.theme, settings.fontScale]);

  useEffect(() => {
    try { setStorageError(!saveState(window.localStorage, settings)); } catch { setStorageError(true); }
    document.documentElement.lang = settings.language;
  }, [settings]);

  useEffect(() => {
    const controller = new AbortController();
    setError(false); setLoading(true);
    Promise.all([getData('surahs.json', controller.signal), getData('availability.json', controller.signal), getData('audio-timings.json', controller.signal)])
      .then(([list, available, timings]) => { setSurahs(list); setAvailability(available); setAudioTimings(timings.surahs); setLoading(false); })
      .catch(e => { if (e.name !== 'AbortError') { setError(true); setLoading(false); } });
    return () => controller.abort();
  }, [retry]);

  useEffect(() => {
    if (!surahs.length) return;
    function syncRoute() {
      const next = parseRoute(window.location.hash, surahs);
      pendingSeek.current = next ? audioTimings?.[next.surah]?.starts[next.verse - 1] ?? 0 : null;
      setPlaying(false); setAudioError(false); setRoute(next); setVerse(next?.verse || 1);
      window.scrollTo(0, 0);
    }
    syncRoute();
    window.addEventListener('hashchange', syncRoute);
    return () => window.removeEventListener('hashchange', syncRoute);
  }, [surahs, audioTimings]);

  const remember = useCallback((surah, number) => {
    setSettings(s => s.last?.surah === surah && s.last?.verse === number ? s : { ...s, last: { surah, verse: number } });
  }, []);

  useEffect(() => {
    if (!route) { setText(null); setTranslation(null); setPassages(null); return; }
    const controller = new AbortController();
    setLoading(true); setError(false); setText(null); setTranslation(null); setPassages(null); restoring.current = true;
    const lang = settings.language;
    Promise.all([getData(`arabic/${route.surah}.json`, controller.signal), availability[lang] ? getData(`${lang}/${route.surah}.json`, controller.signal) : Promise.resolve(null), lang === 'tk' && availability.tk ? getData(`tk-passages/${route.surah}.json`, controller.signal) : Promise.resolve(null)])
      .then(([arabic, translated, grouped]) => {
        if (controller.signal.aborted) return;
        setText(arabic); setTranslation(translated); setPassages(grouped); setLoading(false);
        remember(route.surah, route.verse);
      })
      .catch(e => { if (e.name !== 'AbortError') { setError(true); setLoading(false); } });
    return () => controller.abort();
  }, [route, settings.language, availability, retry, remember]);

  useEffect(() => {
    if (!text || !route) return;
    const frame = requestAnimationFrame(() => document.getElementById(`verse-${route.verse}`)?.scrollIntoView({ behavior: 'instant', block: 'start' }));
    const timer = setTimeout(() => { restoring.current = false; }, 450);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, [text, route]);

  useEffect(() => {
    if (!text || !chapter) return;
    let timer;
    const track = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (restoring.current || playing) return;
        const elements = [...document.querySelectorAll('[data-verse]')];
        const visibleTop = (readerToolbar.current?.getBoundingClientRect().bottom || 112) + 8;
        const first = elements.find(el => el.getBoundingClientRect().bottom > visibleTop);
        if (first) {
          const number = Number(first.dataset.verse);
          setVerse(number); remember(chapter.number, number);
          window.history.replaceState(null, '', `#${chapter.number}:${number}`);
        }
      }, 120);
    };
    window.addEventListener('scroll', track, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener('scroll', track); };
  }, [text, chapter, playing, remember]);

  useEffect(() => {
    const player = audio.current;
    if (!chapter || !playing) { player.pause(); return; }
    let cancelled = false;
    player.play().catch(e => { if (!cancelled && e.name !== 'AbortError') { setPlaying(false); setAudioError(true); } });
    return () => { cancelled = true; player.pause(); };
  }, [chapter, playing]);

  useEffect(() => { applyPendingSeek(); }, [route, track]);

  useEffect(() => () => clearTimeout(seekScrollTimer.current), []);

  useEffect(() => { if (audio.current) audio.current.playbackRate = playbackRate; }, [playbackRate]);

  useEffect(() => {
    if (!playing || !chapter) return;
    remember(chapter.number, verse);
    window.history.replaceState(null, '', `#${chapter.number}:${verse}`);
    document.getElementById(`verse-${verse}`)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
  }, [verse, playing, chapter, remember]);

  useLayoutEffect(() => {
    if (!audioBar.current) {
      document.documentElement.style.setProperty('--player-height', '0px');
      return () => document.documentElement.style.removeProperty('--player-height');
    }
    const measure = () => document.documentElement.style.setProperty('--player-height', `${audioBar.current.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(audioBar.current);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty('--player-height'); };
  }, [chapter, playerVisible]);

  useLayoutEffect(() => {
    const measure = () => {
      document.documentElement.style.setProperty('--header-height', `${headerBar.current?.getBoundingClientRect().height || 0}px`);
      document.documentElement.style.setProperty('--reader-toolbar-height', `${readerToolbar.current?.getBoundingClientRect().height || 0}px`);
    };
    const observer = new ResizeObserver(measure);
    if (headerBar.current) observer.observe(headerBar.current);
    if (readerToolbar.current) observer.observe(readerToolbar.current);
    measure();
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-height');
      document.documentElement.style.removeProperty('--reader-toolbar-height');
    };
  }, [chapter]);

  useEffect(() => { document.title = chapter ? `${chapter.englishName} — KeremliSoz` : 'KeremliSoz — Kurany Kerim'; }, [chapter]);

  useEffect(() => {
    if (credits && creditsDialog.current) creditsDialog.current.showModal();
  }, [credits]);

  useEffect(() => {
    recitationStarted.current = false;
    pendingRecitation.current = null;
    setRecitationIntro(false);
    setPlayerVisible(true);
  }, [route?.surah]);

  useEffect(() => {
    if (recitationIntro && recitationDialog.current && !recitationDialog.current.open) recitationDialog.current.showModal();
  }, [recitationIntro]);

  function dismissRecitation() {
    pendingRecitation.current = null;
    recitationDialog.current?.close();
    setRecitationIntro(false);
  }
  function closePlayer() {
    audio.current.pause();
    setPlaying(false);
    setPlayerVisible(false);
    setAudioError(false);
    recitationStarted.current = false;
    dismissRecitation();
  }
  function startPlayback(number, restart) {
    setPlayerVisible(true);
    setAudioError(false);
    if (restart) seekAudio(track.starts[number - 1]);
    else if (audio.current.ended || verseAtTime(track.starts, audio.current.currentTime) !== number) {
      pendingSeek.current = track.starts[number - 1];
      applyPendingSeek();
    }
    setPlaying(true);
  }
  function requestPlayback(number, restart = false) {
    if (!chapter || !track || !text || loading || error) return;
    if (!recitationStarted.current) {
      pendingRecitation.current = { surah: chapter.number, verse: number, restart };
      setRecitationIntro(true);
      return;
    }
    startPlayback(number, restart);
  }
  function confirmRecitation() {
    const requested = pendingRecitation.current;
    dismissRecitation();
    if (!requested || requested.surah !== chapter?.number) return;
    recitationStarted.current = true;
    startPlayback(requested.verse, requested.restart);
  }

  function navigate(surah, number = 1) {
    setPlaying(false); setAudioError(false);
    window.location.hash = `${surah}:${number}`;
  }
  function applyPendingSeek() {
    if (pendingSeek.current === null || !audio.current.readyState) return;
    try { audio.current.currentTime = pendingSeek.current; pendingSeek.current = null; }
    catch { setAudioError(true); }
  }
  function scrollToVerse(number) {
    restoring.current = true;
    clearTimeout(seekScrollTimer.current);
    document.getElementById(`verse-${number}`)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    seekScrollTimer.current = setTimeout(() => { restoring.current = false; }, 900);
  }
  function seekAudio(seconds) {
    if (!track) return;
    const time = Math.max(0, Math.min(seconds, track.duration));
    const number = verseAtTime(track.starts, time);
    pendingSeek.current = time;
    applyPendingSeek();
    setAudioError(false); setVerse(number);
    remember(chapter.number, number);
    window.history.replaceState(null, '', `#${chapter.number}:${number}`);
    scrollToVerse(number);
  }
  function selectVerse(number, play = false) {
    if (play) { requestPlayback(number, true); return; }
    seekAudio(track.starts[number - 1]);
    setPlaying(false);
  }
  function playToggle() {
    if (playing) { setPlaying(false); return; }
    requestPlayback(verse);
  }
  function syncAudioVerse() {
    if (!track || !playing || pendingSeek.current !== null) return;
    const number = verseAtTime(track.starts, audio.current.currentTime);
    if (number !== verse) setVerse(number);
  }
  function finishAudio() {
    setPlaying(false);
    if (chapter) remember(chapter.number, chapter.numberOfAyahs);
  }
  function card(s, compact = false) {
    return <button className={`surah-card ${compact ? 'compact' : ''}`} key={s.number} onClick={() => navigate(s.number)}>
      <span className="surah-number">{s.number}</span>
      <span className="surah-info"><strong>{s.englishName}</strong><span>{s.numberOfAyahs} {t.verses}</span></span>
      <span className="surah-arabic" lang="ar" dir="rtl">{s.name.replace('سُوْرَةُ ', '')}</span>
      {!compact && <ChevronRight size={16} className="card-arrow" />}
    </button>;
  }
  const last = settings.last && surahs.find(s => s.number === settings.last.surah && settings.last.verse <= s.numberOfAyahs);
  const filtered = surahs.filter(s => `${s.number} ${s.englishName} ${s.englishNameTranslation} ${s.name}`.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f'-]/g, '').includes(query.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f'-]/g, '')));

  const displayPassages = text ? (settings.language === 'tk' && passages ? passages : text.map((_, i) => ({ start: i + 1, end: i + 1, text: translation?.[i] || '', notes: '' }))) : [];

  return <>
    <a href="#main" className="skip-link" onClick={e => { e.preventDefault(); document.getElementById('main')?.focus(); }}>{t.read}</a>
    <header className="header" ref={headerBar}><div className="header-inner">
      <a className="brand" href="#home" aria-label={`KeremliSöz — ${t.home}`}><img className="brand-mark" src={`${BASE}/book-mark.svg`} alt="" aria-hidden="true"/><span>Keremli<span className="brand-light">Söz</span></span></a>
      <div className="header-actions"><a href="#home" className={!route ? 'nav-link selected' : 'nav-link'}>{t.home}</a><span className="nav-divider"/><div className="font-size-controls"><button className="theme-toggle" aria-label={t.smallerText} title={t.smallerText} disabled={settings.fontScale <= 0.85} onClick={() => setSettings(s => ({ ...s, fontScale: Math.max(0.85, Math.round((s.fontScale - 0.1) * 100) / 100) }))}><span className="font-size-small" aria-hidden="true">A</span></button><button className="theme-toggle" aria-label={t.largerText} title={t.largerText} disabled={settings.fontScale >= 1.4} onClick={() => setSettings(s => ({ ...s, fontScale: Math.min(1.4, Math.round((s.fontScale + 0.1) * 100) / 100) }))}><span className="font-size-large" aria-hidden="true">A</span></button></div><label className="language-picker"><Globe2 size={16}/><span className="sr-only">{t.language}</span><select value={settings.language} onChange={e => {
        // Keep the visible verse when a translation changes.
        if (route) setRoute({ surah: route.surah, verse });
        setSettings(s => ({ ...s, language: e.target.value }));
      }}><option value="tk">Türkmençe</option><option value="ru">Русский</option><option value="en">English</option></select></label><button className="theme-toggle" aria-label={t.nightMode} title={t.nightMode} aria-pressed={settings.theme === 'dark'} onClick={() => setSettings(s => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' }))}>{settings.theme === 'dark' ? <Sun size={19} aria-hidden="true"/> : <Moon size={19} aria-hidden="true"/>}</button></div>
    </div></header>
    <main id="main" className={route ? 'reader' : 'home'} tabIndex="-1">
      {!route && <>
        <section className="hero"><span className="eyebrow"><span/> KURANY KERIM</span><h1>{t.tagline}</h1><p>{t.intro}</p><div className="hero-bismillah" lang="ar" dir="rtl">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</div></section>
        {last ? <section className="continue-card"><div><p className="eyebrow">{t.last}</p><h2>{last.englishName} <span>· {t.verse} {settings.last.verse}</span></h2></div><button className="primary" onClick={() => navigate(last.number, settings.last.verse)}>{t.continue}<ArrowRight size={17}/></button></section> : <section className="continue-card"><span className="continue-icon"><BookOpen size={25} strokeWidth={1.4}/></span><div><p className="eyebrow">{t.start}</p><h2>Al-Faatiha <span>· 7 {t.verses}</span></h2></div><button className="primary" onClick={() => navigate(1)}>{t.read}<ArrowRight size={17}/></button></section>}
        <section className="popular-section"><div className="section-title"><h2><TrendingUp size={20} aria-hidden="true"/>{t.popular}</h2></div><div className="popular-grid">{POPULAR.map(n => surahs.find(s => s.number === n)).filter(Boolean).map(s => <button className="popular-pill" key={s.number} onClick={() => navigate(s.number)}><span>{s.number}. {s.englishName}</span><ChevronRight size={16} aria-hidden="true"/></button>)}</div></section>
        <section className="all-section"><div className="section-title"><h2>{t.all} <span className="count">114</span></h2><label className="search"><Search size={17}/><span className="sr-only">{t.search}</span><input placeholder={t.search} value={query} onChange={e => setQuery(e.target.value)}/>{query && <button onClick={() => setQuery('')} aria-label="Clear search"><X size={15}/></button>}</label></div><div className="surah-grid">{filtered.map(s => card(s))}</div>{!loading && !error && !filtered.length && <p className="empty">{t.noResults}</p>}</section>
      </>}
      {route && chapter && <>
        <div className="reader-top" ref={readerToolbar}><a href="#home" className="back-link"><ArrowLeft size={17}/>{t.back}</a><label className="surah-switch"><span className="sr-only">{t.browse}</span><select value={chapter.number} onChange={e => navigate(Number(e.target.value))}>{surahs.map(s => <option key={s.number} value={s.number}>{s.number}. {s.englishName}</option>)}</select></label></div>
        <section className="chapter-heading"><p className="eyebrow">{String(chapter.number).padStart(2, '0')} / 114</p><h1>{chapter.englishName}</h1><p>{chapter.numberOfAyahs} {t.verses} <span>·</span> <span lang="ar" dir="rtl">{chapter.name}</span></p></section>
        <div className="reading-tools"><button className="play-inline" disabled={loading || error} onClick={playToggle}>{playing ? <Pause size={16}/> : <Play size={16}/>} {playing ? t.pause : t.play}</button><span className="reciter"><Headphones size={14}/> {t.reciter}</span></div>
        {!availability[settings.language] && <p className="notice" role="status">{t.unavailable}</p>}
        {chapter.number !== 1 && chapter.number !== 9 && <p className="bismillah" lang="ar" dir="rtl">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>}
        {!loading && !error && text && <div className="verses">{displayPassages.map(group => <article key={`${chapter.number}:${group.start}`} className={`verse ${verse >= group.start && verse <= group.end ? 'active-verse' : ''}`}>
          <div className="verse-meta"><button className="verse-ref" onClick={() => selectVerse(group.start)} aria-label={`${t.verse} ${group.start}`}>{chapter.number}:{group.start}{group.end !== group.start ? `–${group.end}` : ''}</button><IconButton label={`${playing && verse >= group.start && verse <= group.end ? t.pause : t.play} · ${t.verse} ${group.start}`} onClick={() => verse >= group.start && verse <= group.end && playing ? setPlaying(false) : selectVerse(group.start, true)}>{playing && verse >= group.start && verse <= group.end ? <Pause size={15}/> : <Play size={15}/>}</IconButton></div>
          <div className="verse-content">{Array.from({length: group.end - group.start + 1}, (_, offset) => group.start + offset).map(number => <p id={`verse-${number}`} data-verse={number} key={number} className={`arabic ${playing && verse === number ? 'reciting' : ''}`} lang="ar" dir="rtl">{text[number - 1]} <span className="ayah-number">﴿{number.toLocaleString('ar-EG')}﴾</span></p>)}{group.text && <p className="translation" lang={settings.language} style={{fontSize: 18 * (settings.fontScale || 1)}}>{group.end !== group.start && <span className="passage-range">{t.verses} {group.start}–{group.end} · </span>}{group.text}</p>}{group.notes && <details className="explanation"><summary>Düşündiriş</summary><p lang="tk">{group.notes.replace(/^Düşündiriş:\s*/, '')}</p></details>}</div>
        </article>)}</div>}
        {!loading && !error && <div className="chapter-navigation"><button disabled={chapter.number === 1} onClick={() => navigate(chapter.number - 1)}><ArrowLeft size={16}/>{t.previous}</button><button disabled={chapter.number === 114} onClick={() => navigate(chapter.number + 1)}>{t.next}<ArrowRight size={16}/></button></div>}
      </>}
      {loading && <p className="empty" role="status">{t.loading}</p>}
      {error && <div className="empty" role="alert"><p>{t.error}</p><button className="primary" onClick={() => setRetry(n => n + 1)}>{t.retry}</button></div>}
      {storageError && <p className="notice" role="status">{t.storageError}</p>}
    </main>
    <footer className={route ? 'footer reader-footer' : 'footer'}><span><Check size={14}/>{t.saved}</span><button onClick={() => setCredits(true)}>{t.sources}</button></footer>
    {route && chapter && playerVisible && <div className="audio-bar" ref={audioBar}>{track && <AudioTimeline key={chapter.number} audioRef={audio} track={track} verse={verse} label={t.seekAudio} onSeek={seekAudio} disabled={loading || error}/>}<div className="audio-inner"><div className="audio-title"><div><strong>{chapter.englishName}</strong><span>{t.verse} {verse} {t.of} {chapter.numberOfAyahs}</span></div></div><div className="playback"><IconButton label={t.previousVerse} disabled={verse === 1 || !text} onClick={() => selectVerse(verse - 1, playing)}><Rewind size={19}/></IconButton><button className="play-button" aria-label={playing ? t.pause : t.play} disabled={loading || error} onClick={playToggle}>{playing ? <Pause size={20} fill="currentColor"/> : <Play size={20} fill="currentColor"/>}</button><IconButton label={t.nextVerse} disabled={verse === chapter.numberOfAyahs || !text} onClick={() => selectVerse(verse + 1, playing)}><FastForward size={19}/></IconButton></div><div className="audio-actions"><IconButton label={t.closePlayer} onClick={closePlayer}><X size={19} aria-hidden="true"/></IconButton><select className="speed-select" aria-label={t.speed} title={t.speed} value={playbackRate} onChange={e => setPlaybackRate(Number(e.target.value))}>{[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(rate => <option key={rate} value={rate}>{rate}×</option>)}</select></div></div>{audioError && <p className="audio-error" role="alert">{t.audioError}</p>}</div>}
    <audio ref={audio} src={chapter ? audioUrl(chapter.number) : undefined} preload="metadata" onLoadedMetadata={applyPendingSeek} onTimeUpdate={syncAudioVerse} onEnded={finishAudio} onError={() => { if (playing) { setPlaying(false); setAudioError(true); } }}/>
    {recitationIntro && <div className="modal-backdrop"><dialog className="recitation-dialog" ref={recitationDialog} aria-labelledby="recitation-title" aria-describedby="recitation-pronunciation" onCancel={event => { event.preventDefault(); dismissRecitation(); }} onClose={() => { pendingRecitation.current = null; setRecitationIntro(false); }} onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dismissRecitation();
    }}>
      <div className="section-title"><h2 id="recitation-title">{t.beforeRecitation}</h2><IconButton label={t.close} autoFocus onClick={dismissRecitation}><X size={20}/></IconButton></div>
      <p className="istiadhah-arabic" lang="ar" dir="rtl">{ISTIADHAH}</p>
      <p className="istiadhah-transliteration" id="recitation-pronunciation" lang={settings.language}>{t.istiadhahTransliteration}</p>
      <p className="istiadhah-arabic" lang="ar" dir="rtl">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>
      <p className="istiadhah-transliteration" lang={settings.language}>{t.bismillahTransliteration}</p>
      <blockquote className="recitation-quote" cite="https://quran.com/16/98">{/* <p className="recitation-quote-arabic" lang="ar" dir="rtl">فَإِذَا قَرَأۡتَ ٱلۡقُرۡءَانَ فَٱسۡتَعِذۡ بِٱللَّهِ مِنَ ٱلشَّيۡطَٰنِ ٱلرَّجِيمِ</p> */}<p lang={settings.language}>“{t.recitationVerse}”</p><cite><a href="#16:98" onClick={dismissRecitation}>{t.recitationVerseReference}</a></cite></blockquote>
      <button className="primary recitation-start" onClick={confirmRecitation}><Play size={17} aria-hidden="true"/>{t.startRecitation}</button>
    </dialog></div>}
    {credits && <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setCredits(false); }}><dialog ref={creditsDialog} aria-labelledby="credits-title" onCancel={() => setCredits(false)} onClose={() => setCredits(false)}><div className="section-title"><h2 id="credits-title">{t.sources}</h2><IconButton label="Close" onClick={() => setCredits(false)}><X size={20}/></IconButton></div><p>Arabic: Uthmani Hafs text. English: Dr. Mustafa Khattab, The Clear Quran® (Allah edition). Russian: Elmir Kuliev.</p><p>Text and surah metadata: <a href="https://github.com/fawazahmed0/quran-api" target="_blank" rel="noreferrer">Quran API</a>. Original text is preserved. English edition copyright: Furqaan Institute of Quranic Education; <a href="https://theclearquran.org/copyright-information/" target="_blank" rel="noreferrer">copyright information</a>.</p><p>Arabic audio: Mishari Rashid Alafasy, continuous original surah recordings from <a href="https://quranicaudio.com" target="_blank" rel="noreferrer">QuranicAudio</a>, with verse timings from <a href="https://quran.com" target="_blank" rel="noreferrer">Quran.com / Quran Foundation</a>.</p><p>Turkmen: <a href="https://qul.tarteel.ai/resources/translation/577" target="_blank" rel="noreferrer">Kurany Kerim Türkmen Dilinde · QUL</a>. Current edition: the supplied 533-page PDF, attributed in its preface to the Office of the Mufti of Turkmenistan. Combined passages and explanatory notes are preserved. The supplied QUL JSON contains incomplete text and incorrect verse mappings. PDF-based repairs are documented in the repository; the current reader retains the PDF edition.</p><p>Your language, night mode, and last verse stay in this browser. Recitation automatically follows the current verse. Clearing browser data resets them.</p><button className="primary" autoFocus onClick={() => setCredits(false)}>OK</button></dialog></div>}
  </>;
}
